/**
 * Tip Management Service for AI Agents
 * Handles tip goals, milestones, rewards, and payment integration
 */

import { AgentAction } from '../core/types';

export interface TipGoal {
  id: string;
  title: string;
  description: string;
  targetAmount: number;
  currentAmount: number;
  currency: 'SOL' | 'NEAR' | 'ETH' | 'USD';
  reward: TipReward;
  isActive: boolean;
  createdAt: Date;
  completedAt?: Date;
  priority: 'low' | 'medium' | 'high';
  category: 'animation' | 'content' | 'interaction' | 'milestone';
}

export interface TipReward {
  type: 'animation' | 'content' | 'special' | 'unlock';
  value: string;
  duration?: number;
  unlocks?: string[];
  message?: string;
}

export interface TipTransaction {
  id: string;
  userId: string;
  amount: number;
  currency: string;
  goalId?: string;
  message?: string;
  timestamp: Date;
  transactionHash?: string;
  status: 'pending' | 'confirmed' | 'failed';
}

export interface TipMilestone {
  amount: number;
  currency: string;
  reward: TipReward;
  message: string;
  achieved: boolean;
  achievedAt?: Date;
}

export interface TipperStats {
  userId: string;
  totalTipped: number;
  favoriteCurrency: string;
  tipCount: number;
  largestTip: number;
  firstTipDate: Date;
  lastTipDate: Date;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';
}

export class TipManagementService {
  private goals: Map<string, TipGoal> = new Map();
  private transactions: Map<string, TipTransaction> = new Map();
  private milestones: TipMilestone[] = [];
  private tipperStats: Map<string, TipperStats> = new Map();
  private totalEarned = { SOL: 0, NEAR: 0, ETH: 0, USD: 0 };

  constructor() {
    this.initializeDefaultMilestones();
  }

  private initializeDefaultMilestones(): void {
    this.milestones = [
      {
        amount: 1,
        currency: 'SOL',
        reward: { type: 'animation', value: 'thank-you-wave', duration: 3000 },
        message: 'Thank you for your first tip! 💖',
        achieved: false,
      },
      {
        amount: 5,
        currency: 'SOL',
        reward: { type: 'animation', value: 'dance-happy', duration: 5000 },
        message: 'Wow! You are amazing! Time to dance! 💃',
        achieved: false,
      },
      {
        amount: 10,
        currency: 'SOL',
        reward: {
          type: 'unlock',
          value: 'special-animations',
          unlocks: ['dance-special', 'pose-cute', 'gesture-heart'],
          message: 'You unlocked special animations! ✨',
        },
        message: 'Special animations unlocked! You are incredible! 🌟',
        achieved: false,
      },
      {
        amount: 25,
        currency: 'SOL',
        reward: { type: 'content', value: 'custom-image-generation' },
        message: 'Custom images unlocked! I can draw anything for you! 🎨',
        achieved: false,
      },
      {
        amount: 50,
        currency: 'SOL',
        reward: {
          type: 'special',
          value: 'vip-access',
          unlocks: ['private-chat', 'custom-personality', 'priority-responses'],
        },
        message: 'VIP access granted! You are truly special! 👑',
        achieved: false,
      },
    ];
  }

  createGoal(goal: Omit<TipGoal, 'id' | 'currentAmount' | 'createdAt'>): string {
    const id = `goal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newGoal: TipGoal = {
      ...goal,
      id,
      currentAmount: 0,
      createdAt: new Date(),
    };

    this.goals.set(id, newGoal);
    return id;
  }

  processTip(
    userId: string,
    amount: number,
    currency: string,
    goalId?: string,
    message?: string
  ): {
    success: boolean;
    actions: AgentAction[];
    milestones: TipMilestone[];
    goalCompleted?: TipGoal;
  } {
    const transactionId = `tx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const transaction: TipTransaction = {
      id: transactionId,
      userId,
      amount,
      currency,
      goalId,
      message,
      timestamp: new Date(),
      status: 'confirmed', // In real implementation, this would be 'pending' initially
    };

    this.transactions.set(transactionId, transaction);

    // Update total earnings
    if (currency in this.totalEarned) {
      this.totalEarned[currency as keyof typeof this.totalEarned] += amount;
    }

    // Update tipper stats
    this.updateTipperStats(userId, amount, currency);

    const actions: AgentAction[] = [];
    const achievedMilestones: TipMilestone[] = [];
    let goalCompleted: TipGoal | undefined;

    // Process goal update if specified
    if (goalId && this.goals.has(goalId)) {
      const goal = this.goals.get(goalId)!;
      goal.currentAmount += amount;

      if (goal.currentAmount >= goal.targetAmount && goal.isActive) {
        goal.isActive = false;
        goal.completedAt = new Date();
        goalCompleted = goal;

        // Add goal completion actions
        actions.push(this.createRewardAction(goal.reward));

        if (goal.reward.message) {
          actions.push({
            type: 'audio',
            action: 'play',
            value: 'goal-completion-sound',
            volume: 0.8,
          });
        }
      }
    }

    // Check for milestone achievements
    const userStats = this.tipperStats.get(userId);
    if (userStats) {
      this.milestones.forEach((milestone) => {
        if (
          !milestone.achieved &&
          userStats.totalTipped >= milestone.amount &&
          userStats.favoriteCurrency === milestone.currency
        ) {
          milestone.achieved = true;
          milestone.achievedAt = new Date();
          achievedMilestones.push(milestone);

          actions.push(this.createRewardAction(milestone.reward));
        }
      });
    }

    // Add basic tip response
    actions.push({
      type: 'vrm',
      action: 'expression',
      value: 'smile-big',
      duration: 3000,
      intensity: 0.9,
    });

    actions.push({
      type: 'vrm',
      action: 'gesture',
      value: 'heart-hands',
      duration: 2000,
    });

    // Add tier-based special animations
    if (amount >= 5) {
      actions.push({
        type: 'canvas',
        action: 'effect',
        value: 'hearts',
        duration: 5000,
      });
    }

    if (amount >= 10) {
      actions.push({
        type: 'vrm',
        action: 'animation',
        value: 'dance-celebration',
        duration: 8000,
      });
    }

    return {
      success: true,
      actions,
      milestones: achievedMilestones,
      goalCompleted,
    };
  }

  private createRewardAction(reward: TipReward): AgentAction {
    switch (reward.type) {
      case 'animation':
        return {
          type: 'vrm',
          action: 'animation',
          value: reward.value,
          duration: reward.duration || 5000,
        };

      case 'content':
        return {
          type: 'canvas',
          action: 'draw',
          value: reward.value,
          position: { x: 0.7, y: 0.3 },
          size: { width: 400, height: 400 },
        };

      case 'special':
        return {
          type: 'audio',
          action: 'play',
          value: 'special-unlock-sound',
          volume: 0.7,
        };

      case 'unlock':
        return {
          type: 'canvas',
          action: 'overlay',
          value: 'unlock-notification',
          duration: 5000,
        };

      default:
        return {
          type: 'vrm',
          action: 'animation',
          value: 'celebration',
          duration: 3000,
        };
    }
  }

  private updateTipperStats(userId: string, amount: number, currency: string): void {
    let stats = this.tipperStats.get(userId);

    if (!stats) {
      stats = {
        userId,
        totalTipped: 0,
        favoriteCurrency: currency,
        tipCount: 0,
        largestTip: 0,
        firstTipDate: new Date(),
        lastTipDate: new Date(),
        tier: 'bronze',
      };
      this.tipperStats.set(userId, stats);
    }

    stats.totalTipped += amount;
    stats.tipCount++;
    stats.lastTipDate = new Date();

    if (amount > stats.largestTip) {
      stats.largestTip = amount;
    }

    // Update tier based on total tipped
    if (stats.totalTipped >= 100) {
      stats.tier = 'diamond';
    } else if (stats.totalTipped >= 50) {
      stats.tier = 'platinum';
    } else if (stats.totalTipped >= 25) {
      stats.tier = 'gold';
    } else if (stats.totalTipped >= 10) {
      stats.tier = 'silver';
    }
  }

  getActiveGoals(): TipGoal[] {
    return Array.from(this.goals.values()).filter((goal) => goal.isActive);
  }

  getCompletedGoals(): TipGoal[] {
    return Array.from(this.goals.values()).filter((goal) => !goal.isActive && goal.completedAt);
  }

  getGoalProgress(goalId: string): { current: number; target: number; percentage: number } | null {
    const goal = this.goals.get(goalId);
    if (!goal) return null;

    return {
      current: goal.currentAmount,
      target: goal.targetAmount,
      percentage: Math.min(100, (goal.currentAmount / goal.targetAmount) * 100),
    };
  }

  getTipperStats(userId: string): TipperStats | null {
    return this.tipperStats.get(userId) || null;
  }

  getTopTippers(limit: number = 10): TipperStats[] {
    return Array.from(this.tipperStats.values())
      .sort((a, b) => b.totalTipped - a.totalTipped)
      .slice(0, limit);
  }

  getTotalEarnings(): Record<string, number> {
    return { ...this.totalEarned };
  }

  getRecentTransactions(limit: number = 20): TipTransaction[] {
    return Array.from(this.transactions.values())
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, limit);
  }

  getMilestoneProgress(): Array<TipMilestone & { progress: number }> {
    const totalSol = this.totalEarned.SOL;
    return this.milestones.map((milestone) => ({
      ...milestone,
      progress: Math.min(100, (totalSol / milestone.amount) * 100),
    }));
  }

  generateTipRequestMessage(goalId?: string): string {
    if (goalId && this.goals.has(goalId)) {
      const goal = this.goals.get(goalId)!;
      const remaining = goal.targetAmount - goal.currentAmount;
      return `Help me reach my goal: "${goal.title}"! Only ${remaining} ${goal.currency} more to unlock ${goal.reward.value}! 💖`;
    }

    const activeGoals = this.getActiveGoals();
    if (activeGoals.length > 0) {
      const goal = activeGoals[0];
      return `I'm working towards: ${goal.title}! Any support helps me learn new things! ✨`;
    }

    return 'Tips help me learn new animations and create better content for you! Every bit of support means the world to me! 💕';
  }

  updateGoal(goalId: string, updates: Partial<TipGoal>): boolean {
    const goal = this.goals.get(goalId);
    if (!goal) return false;

    Object.assign(goal, updates);
    return true;
  }

  deleteGoal(goalId: string): boolean {
    return this.goals.delete(goalId);
  }

  getUserTipHistory(userId: string): TipTransaction[] {
    return Array.from(this.transactions.values())
      .filter((tx) => tx.userId === userId)
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }
}
